import GridLayoutEditor from './GridLayoutEditor.vue'
import { emptyLibrary, activeSpec, sourceColumns, lockedColumn, hardHidden, renderedColumns, resolveLayout, storageKey, readLibrary, cloneLayout } from './layouts'
import { emptyCatalog, catalogKey, readCatalog, combinedLibrary, prepareLayoutSave, commitLayoutStorage } from './layoutAccess'
import { calculateTotals } from './totals'

const storage = { getItem: key => window.localStorage.getItem(key), setItem: (key, value) => window.localStorage.setItem(key, value), removeItem: key => window.localStorage.removeItem(key) }
export default function gridLayouts(system, loadUsers, { remote = false } = {}) {
  return {
    components: { GridLayoutEditor },
    props: { customizable: { type: Boolean, default: true }, layoutKey: { type: String, default: '' } },
    data: () => ({ gridLayoutLibrary: emptyLibrary(), gridLayoutPersonal: emptyLibrary(), gridLayoutCatalog: emptyCatalog(), gridLayoutRaw: null, gridLayoutCatalogRaw: null, gridLayoutAccessInvalid: false, gridLayoutWarning: '', gridLayoutError: '', gridLayoutEditorOpen: false, gridLayoutUsers: [], gridLayoutUsersLoading: false, gridLayoutUsersError: '', gridLayoutUsersVersion: 0, gridLayoutPersonalVersion: 0, gridLayoutCatalogVersion: 0 }),
    computed: {
      gridLayoutUserId() {
        const user = this.$store?.state?.dadosUsuario || {}
        if (system === 'gerencial') {
          const master = ['Proprietário da Conta', 'ACCOUNT', 'Account'].includes(user.entityType) || user.accountType === 'ACCOUNT_OWNER'
          if (master) return user.id == null && user.userId == null ? '' : 'account:' + (user.id ?? user.userId)
          if (user.userId != null) return 'panel:' + user.userId
          if (user.id == null) return ''
          return (Array.isArray(user.permissions) && user.permissions.length ? 'panel:' : 'representative:') + user.id
        }
        const id = user.id ?? user.userId ?? user.usuarioId
        return id == null ? '' : String(id)
      },
      gridLayoutManager() {
        const user = this.$store?.state?.dadosUsuario || {}
        if (!this.gridLayoutUserId) return false
        return system === 'gerencial' ? ['Proprietário da Conta', 'ACCOUNT', 'Account'].includes(user.entityType) || user.accountType === 'ACCOUNT_OWNER' : (user.roles || []).includes('COMPANY_ADMIN') || (user.modules || []).includes('COMPANY_ADMIN') || user.isOwner === true
      },
      gridLayoutScope() {
        const state = this.$store?.state || {}, user = state.dadosUsuario || {}
        let parent = this.$parent
        while (parent && !parent.$options.name) parent = parent.$parent
        const route = this.$route?.name || this.$route?.matched?.slice(-1)[0]?.path || this.$route?.path || 'sem-rota'
        return { system, user: this.gridLayoutUserId, company: state.empresa?.id ?? state.dadosEmpresa?.id ?? user.representativeId ?? user.representanteId ?? 'geral', grid: this.layoutKey || `${route}/${parent?.$options.name || 'grid'}/${this.title}` }
      },
      gridLayoutContextKey() { return storageKey(this.gridLayoutScope) },
      gridLayoutCatalogKey() { return catalogKey(this.gridLayoutScope) },
      gridLayoutContextSignature() { return JSON.stringify([this.gridLayoutContextKey, this.gridLayoutCatalogKey, this.gridLayoutManager]) },
      gridLayoutCatalogSignature() { return JSON.stringify(sourceColumns(this.columns).map(col => [col.key, lockedColumn(col), hardHidden(col), col.sortable !== false])) },
      gridInitialSort() { return { campo: this.initialSort, direcao: this.initialSortDirection === 'desc' ? 'desc' : 'asc' } },
      gridLayoutLockedId() { return this.gridLayoutLibrary.policy?.lockedId || null },
      gridActiveLayout() { return this.customizable || this.gridLayoutLockedId ? activeSpec(this.gridLayoutLibrary) : null },
      gridLayoutName() {
        const active = this.gridLayoutLibrary.active
        return active === 'system' ? 'Padrão do sistema' : active === 'custom' ? 'Personalizado' : this.gridLayoutLibrary.layouts.find(item => item.id === active)?.name || 'Padrão do sistema'
      },
      gridConfigurable() { return this.customizable && sourceColumns(this.columns).some(col => col.label && !hardHidden(col)) },
      gridDefaultTotals() { return this.colunasTotalizadas.map(column => ({ field: String(column.key), operation: 'sum' })) },
      gridTotalizers() {
        if (!this.temLinhas) return []
        if (Array.isArray(this.gridActiveLayout?.totals)) return calculateTotals(this.columns, this.linhasOrdenadas, this.gridActiveLayout.totals)
        return this.colunasTotalizadas.map(column => ({ id: String(column.key) + ':sum', field: String(column.key), label: column.label, formatted: this.formatarTotalColuna(column) }))
      }
    },
    created() { this.loadGridLayouts() },
    mounted() {
      this.gridLayoutStorageListener = event => {
        if (event.sourceGrid === this._uid) return
        if (event.key !== this.gridLayoutContextKey && event.key !== this.gridLayoutCatalogKey) return
        this.gridLayoutEditorOpen = false; this.loadGridLayouts()
      }
      window.addEventListener('storage', this.gridLayoutStorageListener)
      window.addEventListener('grid-layouts-updated', this.gridLayoutStorageListener)
    },
    beforeDestroy() { this.gridLayoutUsersVersion++; window.removeEventListener('storage', this.gridLayoutStorageListener); window.removeEventListener('grid-layouts-updated', this.gridLayoutStorageListener) },
    watch: {
      gridLayoutContextSignature() { this.gridLayoutUsersVersion++; this.gridLayoutEditorOpen = false; this.gridLayoutUsers = []; this.loadGridLayouts() },
      gridLayoutCatalogSignature() { this.gridLayoutEditorOpen = false; this.applyGridLayoutSort() },
      customizable() { this.gridLayoutEditorOpen = false; this.applyGridLayoutSort() }
    },
    methods: {
      loadGridLayouts(preferredActive) {
        const requestVersion = this.gridLayoutLoadVersion = (this.gridLayoutLoadVersion || 0) + 1
        const personal = readLibrary(storage, this.gridLayoutContextKey), shared = readCatalog(storage, this.gridLayoutCatalogKey)
        const user = this.$store?.state?.dadosUsuario || {}
        if (system === 'gerencial' && personal.raw == null && user.id != null && user.userId == null && (this.gridLayoutManager || !user.permissions?.length)) {
          const legacy = readLibrary(storage, storageKey({ ...this.gridLayoutScope, user: user.id }))
          if (legacy.raw != null && !legacy.warning) personal.library = legacy.library
        }
        this.gridLayoutPersonal = personal.library; this.gridLayoutCatalog = shared.catalog
        this.gridLayoutRaw = personal.raw; this.gridLayoutCatalogRaw = shared.raw; this.gridLayoutAccessInvalid = !!shared.invalid
        this.gridLayoutWarning = [personal.warning, shared.warning].filter(Boolean).join(' '); this.gridLayoutError = ''
        this.gridLayoutLibrary = combinedLibrary(personal.library, shared.catalog, this.gridLayoutUserId, this.gridLayoutManager, preferredActive)
        this.applyGridLayoutSort()

        // Sincronização com o backend
        const scope = this.gridLayoutScope
        const signature = this.gridLayoutContextSignature
        const personalKey = this.gridLayoutContextKey, catalogKey = this.gridLayoutCatalogKey
        const personalBefore = this.gridLayoutRaw, catalogBefore = this.gridLayoutCatalogRaw
        if (remote && this.$axios && this.$store?.state?.urlRest && scope?.grid) {
          const url = `${this.$store.state.urlRest}grid-layouts/${encodeURIComponent(scope.system)}/${encodeURIComponent(scope.grid)}`
          const config = scope.company ? { headers: { 'X-Company-Id': scope.company } } : {}
          this.$axios.get(url, config).then(res => {
            if (this._isDestroyed || this._isBeingDestroyed || requestVersion !== this.gridLayoutLoadVersion || signature !== this.gridLayoutContextSignature || this.gridLayoutEditorOpen || personalBefore !== this.gridLayoutRaw || catalogBefore !== this.gridLayoutCatalogRaw) return
            if (!res?.data || typeof res.data !== 'object' || Array.isArray(res.data) || (!Object.prototype.hasOwnProperty.call(res.data, 'personal') && !Object.prototype.hasOwnProperty.call(res.data, 'catalog'))) return
            const { personal: srvPersonal, personalVersion, catalog: srvCatalog, catalogVersion } = res.data
            const invalidPersonal = srvPersonal && readLibrary({ getItem: () => JSON.stringify(srvPersonal) }, 'server').warning
            const invalidCatalog = srvCatalog && readCatalog({ getItem: () => JSON.stringify(srvCatalog) }, 'server').invalid
            if (invalidPersonal || invalidCatalog) { this.gridLayoutWarning = 'Layouts do servidor inválidos. As preferências locais foram preservadas.'; return }
            this.gridLayoutPersonalVersion = personalVersion || 0
            this.gridLayoutCatalogVersion = catalogVersion || 0
            const temLocal = (this.gridLayoutPersonal?.layouts?.length || 0) > 0 || (this.gridLayoutCatalog?.layouts?.length || 0) > 0
            const temSrv = (srvPersonal?.layouts?.length || 0) > 0 || (srvCatalog?.layouts?.length || 0) > 0
            if (!temSrv && temLocal) {
              this.syncGridLayoutsToServer(this.gridLayoutPersonal, this.gridLayoutCatalog)
              return
            }
            if (srvPersonal) {
              this.gridLayoutPersonal = srvPersonal
              this.gridLayoutRaw = JSON.stringify(srvPersonal)
              storage.setItem(personalKey, this.gridLayoutRaw)
            }
            if (srvCatalog) {
              this.gridLayoutCatalog = srvCatalog
              this.gridLayoutCatalogRaw = JSON.stringify(srvCatalog)
              storage.setItem(catalogKey, this.gridLayoutCatalogRaw)
            }
            this.gridLayoutLibrary = combinedLibrary(this.gridLayoutPersonal, this.gridLayoutCatalog, this.gridLayoutUserId, this.gridLayoutManager, preferredActive)
            this.applyGridLayoutSort()
          }).catch(() => {})
        }
      },
      syncGridLayoutsToServer(personal, catalog) {
        const scope = this.gridLayoutScope
        const signature = this.gridLayoutContextSignature
        if (!remote || !this.$axios || !this.$store?.state?.urlRest || !scope?.grid) return
        const url = `${this.$store.state.urlRest}grid-layouts/${encodeURIComponent(scope.system)}/${encodeURIComponent(scope.grid)}`
        const config = scope.company ? { headers: { 'X-Company-Id': scope.company } } : {}
        const reqPayload = {
          personal: personal || null,
          personalVersion: this.gridLayoutPersonalVersion ?? null,
          catalog: this.gridLayoutManager ? (catalog || null) : null,
          catalogVersion: this.gridLayoutCatalogVersion ?? null
        }
        this.$axios.put(url, reqPayload, config).then(res => {
          if (!this._isDestroyed && signature === this.gridLayoutContextSignature && res?.data) {
            this.gridLayoutPersonalVersion = res.data.personalVersion || 0
            this.gridLayoutCatalogVersion = res.data.catalogVersion || 0
          }
        }).catch(() => {})
      },
      applyGridLayoutSort() {
        const sort = this.gridActiveLayout ? resolveLayout(this.columns, this.gridActiveLayout, this.gridInitialSort).sort : this.gridInitialSort
        const changed = this.ordenacao.campo !== sort.campo || this.ordenacao.direcao !== sort.direcao
        if (changed && typeof this.rowKey !== 'function' && (this.rows || []).some(row => row?.[this.rowKey] == null || row[this.rowKey] === '')) this.limparSelecao()
        this.ordenacao = { ...sort }; this.indiceTeclado = -1
      },
      gridRenderedColumns() { return renderedColumns(this.columns, this.gridActiveLayout, this.gridInitialSort) },
      async loadGridLayoutUsers() {
        const version = ++this.gridLayoutUsersVersion, signature = this.gridLayoutContextSignature
        this.gridLayoutUsersError = ''
        const user = this.$store?.state?.dadosUsuario || {}, self = this.gridLayoutUserId ? [{ id: this.gridLayoutUserId, name: user.fullName || user.name || user.nome || 'Você' }] : []
        this.gridLayoutUsers = self; this.gridLayoutUsersLoading = false
        if (!this.gridLayoutManager || !loadUsers) return
        this.gridLayoutUsersLoading = true
        try {
          const records = await loadUsers(this, this.gridLayoutScope.company)
          if (version !== this.gridLayoutUsersVersion || signature !== this.gridLayoutContextSignature || this._isDestroyed) return
          const users = new Map(self.map(item => [item.id, item])); records.forEach(item => users.set(String(item.id), { id: String(item.id), name: String(item.name) }))
          this.gridLayoutUsers = [...users.values()]
        } catch { if (version === this.gridLayoutUsersVersion) this.gridLayoutUsersError = 'Não foi possível carregar os usuários. Os vínculos existentes foram preservados; tente novamente.' }
        finally { if (version === this.gridLayoutUsersVersion) this.gridLayoutUsersLoading = false }
      },
      openGridLayouts() {
        const sort = { ...this.ordenacao }, active = this.gridLayoutLibrary.active
        if (this.gridLayoutContextKey) this.loadGridLayouts(active)
        else this.gridLayoutError = ''
        if (!sort.campo || this.colunasVisiveis.some(col => String(col.key) === String(sort.campo))) this.ordenacao = sort
        this.gridLayoutEditorOpen = true; this.loadGridLayoutUsers()
      },
      async saveGridLayouts(payload) {
        const working = payload.library || payload, close = payload.close !== false
        const signature = this.gridLayoutContextSignature
        const personalKey = this.gridLayoutContextKey, catalogKey = this.gridLayoutCatalogKey
        const personalBefore = this.gridLayoutRaw, catalogBefore = this.gridLayoutCatalogRaw
        try {
          if (this.gridLayoutAccessInvalid) throw Error('Não é possível alterar layouts enquanto as regras de acesso estiverem indisponíveis.')
          const result = prepareLayoutSave(cloneLayout(working), this.gridLayoutPersonal, this.gridLayoutCatalog, this.gridLayoutUserId, this.gridLayoutManager)
          if (this.gridLayoutManager) {
            const known = new Set([...this.gridLayoutUsers.map(item => item.id), ...this.gridLayoutCatalog.layouts.flatMap(item => item.users.map(access => access.userId))])
            if (result.catalog.layouts.some(item => item.users.some(access => !known.has(access.userId)))) throw Error('Carregue os usuários antes de definir novos acessos.')
          }
          if ((personalKey && storage.getItem(personalKey) !== personalBefore) || (catalogKey && storage.getItem(catalogKey) !== catalogBefore)) throw Error('Os layouts foram alterados em outra aba. Recarregue a tela.')
          
          const scope = this.gridLayoutScope
          if (remote && this.$axios && this.$store?.state?.urlRest && scope?.grid) {
            const url = `${this.$store.state.urlRest}grid-layouts/${encodeURIComponent(scope.system)}/${encodeURIComponent(scope.grid)}`
            const config = scope.company ? { headers: { 'X-Company-Id': scope.company } } : {}
            const reqPayload = {
              personal: result.personal,
              personalVersion: this.gridLayoutPersonalVersion ?? null,
              catalog: this.gridLayoutManager ? result.catalog : null,
              catalogVersion: this.gridLayoutCatalogVersion ?? null
            }
            try {
              const res = await this.$axios.put(url, reqPayload, config)
              if (this._isDestroyed || this._isBeingDestroyed || signature !== this.gridLayoutContextSignature) return
              if (res?.data) {
                this.gridLayoutPersonalVersion = res.data.personalVersion || 0
                this.gridLayoutCatalogVersion = res.data.catalogVersion || 0
                if (res.data.personal) result.personal = res.data.personal
                if (res.data.catalog) result.catalog = res.data.catalog
              }
            } catch (err) {
              if (err?.response?.status === 409) {
                throw Error('Os layouts foram alterados em outra aba ou por outro usuário. Recarregue a tela.')
              }
              if (err?.response?.status === 403) {
                throw Error('Acesso negado para alterar o catálogo compartilhado.')
              }
              throw err
            }
          }

          const personalRaw = JSON.stringify(result.personal)
          const catalogRaw = this.gridLayoutManager && (result.catalog.layouts.length || catalogBefore != null) ? JSON.stringify(result.catalog) : catalogBefore
          const updates = [{ key: catalogKey, before: catalogBefore, value: catalogRaw }, { key: personalKey, before: personalBefore, value: personalRaw }]
          commitLayoutStorage(storage, updates)
          this.gridLayoutPersonal = result.personal; this.gridLayoutCatalog = result.catalog
          this.gridLayoutRaw = this.gridLayoutContextKey ? personalRaw : null; this.gridLayoutCatalogRaw = catalogRaw
          this.gridLayoutLibrary = combinedLibrary(result.personal, result.catalog, this.gridLayoutUserId, this.gridLayoutManager, working.active)
          this.gridLayoutError = ''; this.applyGridLayoutSort(); this.gridLayoutEditorOpen = !close
          this.$emit('layout-change', { id: this.gridLayoutLibrary.active, name: this.gridLayoutName })
          for (const key of updates.filter(entry => entry.value !== entry.before).map(entry => entry.key).filter(Boolean)) window.dispatchEvent(Object.assign(new Event('grid-layouts-updated'), { key, sourceGrid: this._uid }))
        } catch (error) {
          this.gridLayoutError = /administrador|fixado|acesso|usuários|outra aba|indisponíveis|favorito|bloqueado|Limite|negado/.test(error.message) ? error.message : 'Não foi possível salvar os layouts no servidor. O rascunho foi mantido.'
        }
      }
    }
  }
}
